import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { CheckCircleIcon, SparklesIcon } from '@heroicons/react/24/solid';
import client from '../../../lib/feathers';
import { setVipEnrolling, setVipError } from '../../../lib/vipSlice';
import VIPAgreement from './VIPAgreement';

export default function VIPEnrollmentView({ vipStatus, listing, onEnrollSuccess }) {
  const dispatch = useDispatch();
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [enrolling, setEnrolling] = useState(false);

  const handleEnroll = async () => {
    if (!agreementAccepted) {
      alert('Please accept the VIP Partner Agreement to continue');
      return;
    }

    try {
      setEnrolling(true);
      dispatch(setVipEnrolling(true));

      const service = client.service('industry/listing-vip-join');
      await service.create({
        listingId: listing.id,
        agreementAccepted: true
      });

      // Refresh VIP status to show enrolled view
      onEnrollSuccess();
    } catch (e) {
      console.error('Error enrolling in VIP:', e);
      dispatch(setVipError(e.message || 'Failed to enroll in VIP Network'));
      alert(`Error: ${e.message || 'Failed to enroll in VIP Network'}`);
    } finally {
      setEnrolling(false);
      dispatch(setVipEnrolling(false));
    }
  };

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#630801] to-[#7E9B0C] rounded-lg p-8 text-white">
        <div className="flex items-center gap-3 mb-4">
          <SparklesIcon className="h-10 w-10" />
          <h2 className="text-3xl font-bold">Join the VinoSeeker VIP Network</h2>
        </div>
        <p className="text-lg text-white/90">
          Attract qualified, ready-to-spend wine enthusiasts to your tasting room
        </p>
      </div>

      {/* Benefits Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Why Join?</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3">
            <CheckCircleIcon className="h-6 w-6 text-[#7E9B0C] flex-shrink-0 mt-1" />
            <div>
              <p className="font-medium text-gray-900">Free to join</p>
              <p className="text-sm text-gray-600">No cost to participate</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircleIcon className="h-6 w-6 text-[#7E9B0C] flex-shrink-0 mt-1" />
            <div>
              <p className="font-medium text-gray-900">VIP badge on your listing</p>
              <p className="text-sm text-gray-600">Stand out on listings and map</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircleIcon className="h-6 w-6 text-[#7E9B0C] flex-shrink-0 mt-1" />
            <div>
              <p className="font-medium text-gray-900">Qualified visitors</p>
              <p className="text-sm text-gray-600">Members pay $30/month for access</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircleIcon className="h-6 w-6 text-[#7E9B0C] flex-shrink-0 mt-1" />
            <div>
              <p className="font-medium text-gray-900">Increased visibility</p>
              <p className="text-sm text-gray-600">Priority in search results</p>
            </div>
          </div>
        </div>
      </div>

      {/* Standard VIP Offer */}
      <div className="bg-[#7E9B0C]/10 rounded-lg border border-[#7E9B0C]/30 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Standard VIP Offer</h3>
        <div className="bg-white rounded-md p-4 mb-3">
          <p className="font-medium text-gray-900 mb-2">VIP Members Receive:</p>
          <ul className="space-y-1 text-gray-700">
            <li className="flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-[#7E9B0C]" />
              Buy-one-get-one tastings
            </li>
            <li className="flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-[#7E9B0C]" />
              10% off bottle purchases
            </li>
          </ul>
        </div>
        <p className="text-sm text-gray-600">
          Members can visit your winery twice per year (once every 6 months)
        </p>
      </div>

      {/* Agreement Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">VIP Partner Agreement</h3>

        <button
          onClick={() => setShowAgreement(!showAgreement)}
          className="text-[#630801] hover:text-[#7E9B0C] font-medium mb-4 underline"
        >
          {showAgreement ? 'Hide Agreement' : 'View Full Agreement'}
        </button>

        {showAgreement && <VIPAgreement />}

        <div className="mt-4 flex items-start gap-3">
          <input
            type="checkbox"
            id="agreement"
            checked={agreementAccepted}
            onChange={(e) => setAgreementAccepted(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-gray-300 text-[#630801] focus:ring-[#630801]"
          />
          <label htmlFor="agreement" className="text-sm text-gray-700">
            I have read and agree to the VIP Partner Agreement
          </label>
        </div>

        <button
          onClick={handleEnroll}
          disabled={!agreementAccepted || enrolling}
          className="mt-6 w-full bg-[#630801] text-white py-3 px-4 rounded-md font-medium hover:bg-[#7E9B0C] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {enrolling ? 'Joining VIP Network...' : 'Join VIP Network'}
        </button>
      </div>
    </div>
  );
}
